import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { obterResultadosPainel, obterRetornoComparado } from "../services/api";

/**
 * RESULTADOS DAS VAGAS (19/09/2026). O que o eaiRH fez pelas vagas da empresa: quantas pessoas abriram cada vaga,
 * quantas foram se candidatar (aqui ou no site da empresa), quantas receberam retorno — e a evolução por dia.
 *
 * Existe porque a empresa não tinha como ver isso, e é o número dela que a convence a se engajar (responder,
 * dar retorno). Os dados já existiam; só não chegavam a ela.
 */
export default function ResultadosEmpresa() {
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    setDados(null); setErro("");
    obterResultadosPainel(dias).then(setDados).catch((e) => setErro(e.message));
  }, [dias]);

  if (erro) return <p role="alert" className="text-sm text-red-600">{erro}</p>;

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {dados ? <>Suas vagas no eaiRH nos últimos <b>{dados.dias} dias</b> · {dados.vagasAtivas} vaga(s) no ar agora</> : "Carregando..."}
        </p>
        <div className="flex gap-2">
          {[7, 30, 90].map((n) => (
            <button key={n} onClick={() => setDias(n)}
              className={`rounded-full px-3 py-1 text-sm font-semibold border ${dias === n
                ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-200 hover:text-gray-900"}`}>
              {n} dias
            </button>
          ))}
        </div>
      </div>

      {dados && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card v={dados.totais.visualizacoes} l="visualizações das vagas" cor="text-gray-800" />
          <Card v={dados.totais.candidaturas + dados.totais.cliquesCandidatar} l="foram se candidatar" cor="text-primary" />
          <Card v={dados.totais.respondidas} l="candidaturas com retorno" cor="text-green-600" />
          <Card v={dados.totais.interesses} l="demonstraram interesse" cor="text-blue-600" />
        </div>

        <RetornoComparado />

        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-900">Dia a dia</h2>
            <div className="flex gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-gray-300" /> visualizações</span>
              <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" /> foram se candidatar</span>
            </div>
          </div>
          <Serie serie={dados.serie} />
        </section>

        <PorVaga vagas={dados.vagas} />

        <p className="mt-4 text-xs text-gray-500 leading-relaxed">
          "Foram se candidatar" soma quem se candidatou pelo eaiRH e quem clicou para se candidatar no site da sua
          empresa. As visualizações de quem já tem conta no eaiRH passaram a ser contadas em 19/09/2026 — antes
          disso, o número mostra só os visitantes sem conta. Nenhum dado pessoal de quem visualizou é registrado.
        </p>
      </>)}
    </>
  );
}

/**
 * TABELA "POR VAGA" — com filtro, ordenação e o detalhe de cada vaga (20/09/2026, pedido do Nelson:
 * "falta um link para clicar na vaga e abrir os dados dela" e "falta um filtro de vaga para ajudar a empresa").
 *
 * O título já levava à vaga PÚBLICA, mas não era isso que faltava: a empresa queria abrir os NÚMEROS daquela
 * vaga. Agora a linha abre, e o que ela abre não é mais número — é a leitura dos números que a empresa não
 * faz sozinha: quantos viram e não foram, quantos foram pelo site dela (e por isso somem do nosso radar) e,
 * acima de tudo, QUANTOS AINDA ESPERAM RESPOSTA. Essa última linha é o eaiRH inteiro numa frase.
 *
 * Tudo em memória: os dados das 100 vagas já vieram na mesma resposta, então filtrar e ordenar não custa
 * nenhuma volta ao servidor.
 */
function PorVaga({ vagas }) {
  const [busca, setBusca] = useState("");
  const [so, setSo] = useState("todas");        // todas | ar | esperando
  const [ordem, setOrdem] = useState("relevancia");
  const [aberta, setAberta] = useState(null);

  const semAcento = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const termo = semAcento(busca.trim());
  const esperando = (v) => Math.max(0, v.candidaturas - v.respondidas);

  const lista = vagas
    .filter((v) => (!termo || semAcento(`${v.titulo} ${v.cidade || ""}`).includes(termo)))
    .filter((v) => (so === "ar" ? v.ativa : so === "esperando" ? esperando(v) > 0 : true));

  const COLUNAS = [
    { k: "visualizacoes", t: "Visualizações", v: (v) => v.visualizacoes, cls: "" },
    { k: "candidatar", t: "Foram se candidatar", v: (v) => v.candidaturas + v.cliquesCandidatar, cls: "font-semibold text-primary" },
    { k: "respondidas", t: "Com retorno", v: (v) => v.respondidas, cls: "text-green-700" },
    { k: "interesses", t: "Interesse", v: (v) => v.interesses, cls: "" },
  ];
  // "relevancia" é a ordem que o servidor já mandou (movimento ponderado) — clicar numa coluna troca por ela.
  const ordenada = ordem === "relevancia" ? lista
    : [...lista].sort((a, b) => COLUNAS.find((c) => c.k === ordem).v(b) - COLUNAS.find((c) => c.k === ordem).v(a));

  const totalEsperando = vagas.reduce((s, v) => s + esperando(v), 0);

  return (
    <section className="rounded-2xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">Por vaga</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="filtrar por vaga ou cidade"
            aria-label="Filtrar vagas" className="w-52 rounded-full border border-gray-200 px-3 py-1 text-sm focus:border-primary focus:outline-none" />
          {[["todas", "todas"], ["ar", "no ar"], ["esperando", totalEsperando > 0 ? `esperando resposta (${totalEsperando})` : "esperando resposta"]].map(([k, t]) => (
            <button key={k} onClick={() => setSo(k)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${so === k
                ? "border-primary bg-primary text-white" : "border-gray-200 bg-white text-gray-600 hover:text-gray-900"}`}>{t}</button>
          ))}
        </div>
      </div>

      {vagas.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">Nenhuma vaga teve movimento neste período.</p>
      ) : ordenada.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">Nenhuma vaga com esse filtro.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 font-semibold">Vaga</th>
                {COLUNAS.map((c) => (
                  <th key={c.k} className="px-3 py-2 text-right font-semibold">
                    <button onClick={() => setOrdem(ordem === c.k ? "relevancia" : c.k)}
                      className={`uppercase tracking-wide hover:text-gray-900 ${ordem === c.k ? "text-primary" : ""}`}
                      title={ordem === c.k ? "Voltar à ordem padrão" : `Ordenar por ${c.t.toLowerCase()}`}>
                      {c.t}{ordem === c.k && " ↓"}
                    </button>
                  </th>
                ))}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" style={{ fontVariantNumeric: "tabular-nums" }}>
              {ordenada.map((v) => (
                <Fragment key={v.id}>
                  <tr className={`cursor-pointer hover:bg-gray-50 ${aberta === v.id ? "bg-gray-50" : ""}`}
                    onClick={() => setAberta(aberta === v.id ? null : v.id)}>
                    <td className="px-4 py-2">
                      <span className="font-medium text-gray-900">{v.titulo}</span>
                      <div className="text-xs text-gray-500">
                        {v.cidade || "—"}{!v.ativa && " · fora do ar"}
                        {esperando(v) > 0 && <span className="ml-1 text-amber-700">· {esperando(v)} esperando resposta</span>}
                      </div>
                    </td>
                    {COLUNAS.map((c) => <td key={c.k} className={`px-3 py-2 text-right ${c.cls}`}>{c.v(v)}</td>)}
                    <td className="px-3 py-2 text-right text-gray-400" aria-hidden>{aberta === v.id ? "▴" : "▾"}</td>
                  </tr>
                  {aberta === v.id && (
                    <tr><td colSpan={6} className="bg-gray-50 px-4 pb-4 pt-1"><DetalheVaga v={v} esperando={esperando(v)} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// O detalhe NÃO repete os números da linha: ele diz o que eles significam. Cada frase só aparece quando o
// número existe — texto sobre zero é ruído, e ruído faz a empresa fechar a tela.
function DetalheVaga({ v, esperando }) {
  const foram = v.candidaturas + v.cliquesCandidatar;
  // 100% só quando é tudo: 199 de 200 arredondava para "100%" ao lado de "1 pessoa ainda espera" (31/10/2026).
  const pct = (a, b) => (b > 0 ? (a < b ? Math.min(99, Math.round((a / b) * 100)) : 100) : 0);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">O caminho de quem viu</h3>
          <ul className="space-y-1.5 text-sm text-gray-700">
            <li><b>{v.visualizacoes}</b> abriram esta vaga</li>
            <li>
              <b>{foram}</b> foram se candidatar
              {v.visualizacoes > 0 && <span className="text-gray-500"> — {pct(foram, v.visualizacoes)}% de quem viu</span>}
            </li>
            {v.cliquesCandidatar > 0 && (
              <li className="text-gray-600">
                └ <b>{v.candidaturas}</b> pelo eaiRH e <b>{v.cliquesCandidatar}</b> pelo site da sua empresa
                <div className="text-xs text-gray-500">Quem sai pelo seu site a gente não consegue acompanhar: o retorno fica só com você.</div>
              </li>
            )}
            {v.interesses > 0 && <li><b>{v.interesses}</b> demonstraram interesse sem se candidatar</li>}
          </ul>
        </div>
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">O retorno</h3>
          {v.candidaturas === 0 ? (
            <p className="text-sm text-gray-500">Ninguém se candidatou pelo eaiRH nesta vaga no período, então não há retorno a acompanhar aqui.</p>
          ) : (
            <ul className="space-y-1.5 text-sm text-gray-700">
              <li><b className="text-green-700">{v.respondidas}</b> receberam retorno seu — {pct(v.respondidas, v.candidaturas)}% das candidaturas pelo eaiRH</li>
              {esperando > 0 && (
                <li className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
                  <b>{esperando} pessoa{esperando > 1 ? "s" : ""} ainda espera{esperando > 1 ? "m" : ""} resposta.</b>
                  <div className="text-xs">Responder — mesmo que seja "não" — é o que constrói sua reputação no eaiRH.</div>
                </li>
              )}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            <Link to={`/vagas/${v.id}`} className="font-semibold text-primary hover:underline">ver a vaga como o candidato vê →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 📊 SEU RETORNO x A MÉDIA DO eaiRH (03/11/2026). "12 com retorno" sozinho não diz se é bom ou ruim; ao lado da
 * média das empresas no portal, diz. Só a MÉDIA aparece, nunca o nome de outra empresa nem posição em ranking.
 *
 * Carrega uma vez, fora do filtro de 7/30/90 dias: a comparação usa janela fixa (180 dias, sem a última semana),
 * senão quase ninguém teria amostra no filtro de 7 dias. Abaixo da amostra mínima a tela diz "ainda sem dados
 * suficientes" em vez de um número: "100% de retorno" com 1 candidatura é verdade e engana.
 */
function RetornoComparado() {
  const [d, setD] = useState(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => { obterRetornoComparado().then(setD).catch(() => setFalhou(true)); }, []);
  // A comparação é complemento: se ela falhar, o resto da aba segue de pé, sem alarme.
  if (falhou || !d) return null;

  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
      <h2 className="font-semibold text-gray-900">Seu retorno x a média do eaiRH</h2>
      <p className="mb-3 text-xs text-gray-500">
        Candidaturas pelo eaiRH nos últimos {d.janelaDias} dias, sem contar a última semana (ainda dentro do prazo de resposta).
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <LinhaTempo emp={d.empresa} mer={d.mercado} min={d.minimo} />
        <LinhaPerc emp={d.empresa} mer={d.mercado} min={d.minimo} />
      </div>

      {d.porArea.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Na mesma área</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            {d.porArea.map((a) => (
              <li key={a.area} className="rounded-xl bg-gray-50 px-3 py-2">
                <b className="text-gray-900">{a.area}</b>
                <div className="text-xs text-gray-600">
                  {a.empresa.diasMedio != null && a.mercado.diasMedio != null && (
                    <span className="mr-3">retorno em {dias(a.empresa.diasMedio)} · média da área: {dias(a.mercado.diasMedio)} ({leituraTempo(a.empresa.diasMedio, a.mercado.diasMedio)})</span>
                  )}
                  {a.empresa.percRetorno != null && a.mercado.percRetorno != null && (
                    <span>{pctTxt(a.empresa.percRetorno)} com retorno · média da área: {pctTxt(a.mercado.percRetorno)} ({leituraPerc(a.empresa.percRetorno, a.mercado.percRetorno)})</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500 leading-relaxed">
        Retorno é quando a sua empresa falou com a pessoa: pelo eaiRH, pelo seu sistema de recrutamento ou pelo seu
        canal, desde que o candidato não tenha dito o contrário. Aviso enviado pelo eaiRH não conta como retorno seu.
        A média considera só empresas com pelo menos {d.minimo.candidaturas} candidaturas no período, e cada empresa
        conta igual, seja qual for o tamanho.
      </p>
    </section>
  );
}

// Leitura em palavras: a empresa não deveria ter que fazer a conta para saber se o número é bom.
// Meio dia ou 3 pontos de diferença é ruído de amostra, e chamar isso de "mais rápido" seria vender precisão.
const leituraTempo = (e, m) => (Math.abs(e - m) < 0.5 ? "no ritmo da média" : e < m ? "mais rápido que a média" : "mais lento que a média");
const leituraPerc = (e, m) => (Math.abs(e - m) < 3 ? "na média" : e > m ? "acima da média" : "abaixo da média");
// Cor só quando a diferença passou do ruído: "na média" em verde seria elogio, em âmbar seria bronca, e não é nenhum dos dois.
const TOM = { "mais rápido que a média": "text-green-700", "acima da média": "text-green-700",
  "mais lento que a média": "text-amber-700", "abaixo da média": "text-amber-700" };
const dias = (n) => { const r = Math.round(n * 10) / 10; return `${r.toLocaleString("pt-BR")} ${r === 1 ? "dia" : "dias"}`; };
const pctTxt = (n) => `${Math.round(n).toLocaleString("pt-BR")}%`;

function LinhaTempo({ emp, mer, min }) {
  return (
    <Comparacao titulo="Tempo médio até o primeiro retorno" rotulo="Seu tempo médio de retorno"
      seu={emp.diasMedio != null ? dias(emp.diasMedio) : null}
      media={mer.diasMedio != null ? dias(mer.diasMedio) : null}
      leitura={emp.diasMedio != null && mer.diasMedio != null ? leituraTempo(emp.diasMedio, mer.diasMedio) : null}
      falta={`Precisa de pelo menos ${min.retornos} retornos com data registrada (hoje: ${emp.retornosComData}).`} />
  );
}

function LinhaPerc({ emp, mer, min }) {
  return (
    <Comparacao titulo="Candidaturas com retorno" rotulo="Suas candidaturas com retorno"
      seu={emp.percRetorno != null ? pctTxt(emp.percRetorno) : null}
      media={mer.percRetorno != null ? pctTxt(mer.percRetorno) : null}
      leitura={emp.percRetorno != null && mer.percRetorno != null ? leituraPerc(emp.percRetorno, mer.percRetorno) : null}
      falta={`Precisa de pelo menos ${min.candidaturas} candidaturas no período (hoje: ${emp.candidaturas}).`} />
  );
}

function Comparacao({ titulo, rotulo, seu, media, leitura, falta }) {
  return (
    <div className="rounded-xl border border-gray-100 p-3">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{titulo}</div>
      <p className="text-sm text-gray-700" style={{ fontVariantNumeric: "tabular-nums" }}>
        {rotulo}: <b className="text-gray-900">{seu ?? "ainda sem dados suficientes"}</b>
        {" · "}Média no eaiRH: <b className="text-gray-900">{media ?? "ainda sem dados suficientes"}</b>
      </p>
      {leitura && <p className={`mt-1 text-sm font-semibold ${TOM[leitura] || "text-gray-700"}`}>{leitura[0].toUpperCase() + leitura.slice(1)}.</p>}
      {!seu && <p className="mt-1 text-xs text-gray-500">{falta}</p>}
    </div>
  );
}

function Card({ v, l, cor }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 text-center">
      <div className={`text-2xl font-bold ${cor}`} style={{ fontVariantNumeric: "tabular-nums" }}>{v.toLocaleString("pt-BR")}</div>
      <div className="text-xs text-gray-500">{l}</div>
    </div>
  );
}

// Barras por dia: visualizações ao fundo (cinza) e quem foi se candidatar na frente (cor da marca), na MESMA
// escala — assim a proporção entre as duas é a que se vê. O rótulo do eixo mostra só o máximo e o zero.
function Serie({ serie }) {
  const max = Math.max(1, ...serie.map((s) => Math.max(s.visualizacoes, s.candidaturas)));
  const W = 600, H = 140, base = H - 18, larg = W / serie.length;
  const y = (n) => base - (n / max) * (base - 8);
  const dm = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };
  const marcas = serie.length <= 7 ? serie.map((_, i) => i) : [0, Math.floor(serie.length / 2), serie.length - 1];
  return (
    <svg viewBox={`0 0 ${W + 34} ${H}`} className="w-full h-auto" role="img"
      aria-label={`Visualizações e candidaturas por dia; máximo de ${max} num dia`}>
      <line x1="34" x2={W + 34} y1={base} y2={base} stroke="#e5e7eb" />
      <text x="28" y={y(max) + 4} textAnchor="end" fontSize="10" fill="#6b7280">{max}</text>
      <text x="28" y={base + 4} textAnchor="end" fontSize="10" fill="#6b7280">0</text>
      {serie.map((s, i) => {
        const x = 34 + i * larg + larg * 0.15, w = larg * 0.7;
        return (
          <g key={s.dia}>
            <title>{`${dm(s.dia)}: ${s.visualizacoes} visualizações, ${s.candidaturas} foram se candidatar`}</title>
            <rect x={x} y={y(s.visualizacoes)} width={w} height={base - y(s.visualizacoes)} fill="#d1d5db" rx="1.5" />
            <rect x={x + w * 0.2} y={y(s.candidaturas)} width={w * 0.6} height={base - y(s.candidaturas)} className="fill-primary" rx="1.5" />
          </g>
        );
      })}
      {marcas.map((i) => (
        <text key={i} x={34 + i * larg + larg / 2} y={H - 4} textAnchor="middle" fontSize="10" fill="#6b7280">{dm(serie[i].dia)}</text>
      ))}
    </svg>
  );
}
